import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { Intervention } from '@hch/shared';
import { Button } from '~/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '~/components/ui/primitives';
import { DataTable, type Column } from '~/components/shared/data-table';
import { PageHeader, StatusBadge } from '~/components/shared/states';
import { useInterventions } from '~/lib/queries';
import { formatPrice, formatRelativeDay, formatSlotRange, fullName } from '~/lib/utils';

const COLONNES: Array<Column<Intervention>> = [
  { header: 'Reference', cell: (i) => i.reference, className: 'font-mono text-xs' },
  {
    header: 'Date',
    cell: (i) => (
      <>
        <span className="capitalize">{formatRelativeDay(i.startsAt)}</span>
        <br />
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatSlotRange(i.startsAt, i.endsAt)}
        </span>
      </>
    ),
    nowrap: true,
  },
  { header: 'Client', cell: (i) => fullName(i.client) },
  { header: 'Technicien', cell: (i) => fullName(i.technician) },
  { header: 'Prestation', cell: (i) => i.packageName, className: 'max-w-48 truncate' },
  { header: 'Statut', cell: (i) => <StatusBadge status={i.status} /> },
  {
    header: 'Montant',
    cell: (i) => formatPrice(i.totalAmount),
    align: 'right',
    className: 'tabular-nums',
  },
  {
    header: '',
    cell: (i) => (
      <Button asChild variant="ghost" size="icon" aria-label="Ouvrir le dossier">
        <Link to={`/admin/interventions/${i.id}`}>
          <ChevronRight />
        </Link>
      </Button>
    ),
  },
];

export function AdminInterventionsPage() {
  const [scope, setScope] = React.useState('upcoming');
  const { data, isLoading, error, refetch } = useInterventions({ scope });

  return (
    <div className="app-page">
      <PageHeader
        title="Interventions"
        description="Toutes les interventions, tous techniciens confondus."
      />

      <Tabs value={scope} onValueChange={setScope}>
        <TabsList>
          <TabsTrigger value="today">Aujourd'hui</TabsTrigger>
          <TabsTrigger value="upcoming">A venir</TabsTrigger>
          <TabsTrigger value="past">Passees</TabsTrigger>
          <TabsTrigger value="all">Toutes</TabsTrigger>
        </TabsList>
      </Tabs>

      <DataTable
        columns={COLONNES}
        rows={data}
        rowKey={(i) => i.id}
        isLoading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        emptyTitle="Aucune intervention sur cette periode"
      />
    </div>
  );
}
